// src/js/pointcloud/deckOverlayManager.ts
// deck.gl MapboxOverlay を MapLibre GL JS 上で管理するモジュール
// 3D Tiles点群データの表示・更新・削除を担当

import { MapboxOverlay } from '@deck.gl/mapbox'
import { Tile3DLayer } from '@deck.gl/geo-layers'
import { Tiles3DLoader } from '@loaders.gl/3d-tiles'
import type { Tileset3D, Tile3D } from '@loaders.gl/tiles'
import type { Map as MaplibreMap } from 'maplibre-gl'

// MapboxOverlay singleton（map毎に1つ）
const overlayMap = new WeakMap<MaplibreMap, MapboxOverlay>()

// アクティブなレイヤー管理
const activeLayers = new WeakMap<MaplibreMap, Map<string, Tile3DLayer>>()

// レイヤーのz-order制御用beforeId
// deck.glの_resolveLayers()がこの値を使ってMapLibreのレイヤースタック内での位置を決定する
const layerBeforeIds = new WeakMap<MaplibreMap, Map<string, string>>()


/**
 * MapboxOverlayを取得または作成
 */
function getOrCreateOverlay(map: MaplibreMap): MapboxOverlay {
  let overlay = overlayMap.get(map)
  if (!overlay) {
    // interleaved: true — MapLibreのレイヤースタックに直接参加
    // deck.glレイヤーがMapLibreのcustomレイヤーとして登録され、
    // moveLayer()によるz-order制御（レイヤー順序変更）が可能になる
    overlay = new MapboxOverlay({
      interleaved: true,
    })
    map.addControl(overlay as unknown as maplibregl.IControl)
    overlayMap.set(map, overlay)
    activeLayers.set(map, new Map())

  }
  return overlay
}

/**
 * 全レイヤーをoverlayに同期
 * beforeIdが設定されている場合、layer.clone({ beforeId }) で位置情報を付与
 * → deck.glの_resolveLayers()がMapLibreのレイヤースタック内で正しい位置に配置する
 */
function syncLayers(map: MaplibreMap): void {
  const overlay = overlayMap.get(map)
  const layers = activeLayers.get(map)
  if (!overlay || !layers) return

  const beforeIds = layerBeforeIds.get(map)
  const layerArray = Array.from(layers.entries()).map(([key, layer]) => {
    const beforeId = beforeIds?.get(key)
    if (beforeId) {
      // beforeIdはdeck.glのMapboxOverlay内部で使われるプロパティ
      // Tile3DLayerの型定義には含まれないため型アサーションが必要
      return layer.clone({ beforeId } as Record<string, unknown>)
    }
    return layer
  })
  overlay.setProps({
    layers: layerArray,
  })
}

export interface PointCloudLayerOptions {
  id: string
  tilesetUrl: string
  pointSize?: number       // default 2
  opacity?: number          // default 1
  geoidHeight?: number      // default 0（楕円体高データのみ正値を指定）
  skipManifest?: boolean    // manifest.json を探索しない（建物タイル等）
  onTilesetLoad?: (tileset: Tileset3D) => void
  onTileLoad?: (tile: Tile3D) => void
  onTileError?: (tile: Tile3D, url: string, message: string) => void
}

/**
 * 1つのTile3DLayerを作成する内部関数
 */
function createTile3DLayer(
  layerId: string,
  tilesetUrl: string,
  pointSize: number,
  opacity: number,
  geoidHeight: number,
  callbacks: {
    onTilesetLoad?: (tileset: Tileset3D) => void
    onTileLoad?: (tile: Tile3D) => void
    onTileError?: (tile: Tile3D, url: string, message: string) => void
  }
): Tile3DLayer {
  return new Tile3DLayer({
    id: layerId,
    data: tilesetUrl,
    loader: Tiles3DLoader,
    pointSize,
    opacity,
    // interleaved モードではMapLibreの地面が深度バッファに書き込み、
    // ピッチ0(真上視点)で点群がz-fightingで消える問題がある
    // depthCompare: 'always' で深度テストをバイパスし、常に描画する
    parameters: {
      depthCompare: 'always',
    },
    // ジオイド高補正（geoidHeight > 0 の場合のみ適用）
    // py3dtiles等でEPSG:6676→EPSG:4978変換した場合、ジオイドモデル未適用なら
    // cartographicOriginのzは既に正高相当 → geoidHeight=0（補正不要）
    // 楕円体高で格納されたデータのみ geoidHeight > 0 を指定する
    onTileLoad: (tile: Tile3D) => {
      try {
        if (tile.content && geoidHeight > 0) {
          const origin = tile.content.cartographicOrigin
          if (origin) {
            origin[2] -= geoidHeight
          }
        }
      } catch (e) {
        console.warn('[PointCloud] onTileLoad height correction failed:', e)
      }
      callbacks.onTileLoad?.(tile)
    },
    onTilesetLoad: (tileset: Tileset3D) => {
      callbacks.onTilesetLoad?.(tileset)
    },
    onTileError: (tile: Tile3D, url: string, message: string) => {
      console.warn(`[PointCloud] タイル読み込みエラー: ${url}`, message)
      callbacks.onTileError?.(tile, url, message)
    },
  })
}

/**
 * 点群レイヤーをマップに追加
 * tilesetUrl が manifest.json を指す場合、各バッチを個別のTile3DLayerとして読み込む
 */
export function addPointCloudLayer(
  map: MaplibreMap,
  options: PointCloudLayerOptions
): void {
  const overlay = getOrCreateOverlay(map)
  const layers = activeLayers.get(map)!
  const {
    id,
    tilesetUrl,
    pointSize = 2,
    opacity = 1,
    geoidHeight = 0,
    skipManifest = false,
    onTilesetLoad,
    onTileLoad,
    onTileError,
  } = options


  // 既存のレイヤーがあれば削除（マルチバッチ分も含めて）
  for (const key of Array.from(layers.keys())) {
    if (key === id || key.startsWith(id + '/')) {
      layers.delete(key)
    }
  }

  const callbacks = { onTilesetLoad, onTileLoad, onTileError }

  if (skipManifest) {
    // manifest.json を探索しない（建物タイル等）
    const layer = createTile3DLayer(
      `pointcloud-${id}`, tilesetUrl, pointSize, opacity, geoidHeight, callbacks
    )
    layers.set(id, layer)
    syncLayers(map)
    return
  }

  // manifest.json パターン判定: tilesetUrl の隣に manifest.json があるか試す
  const baseDir = tilesetUrl.substring(0, tilesetUrl.lastIndexOf('/'))
  const manifestUrl = baseDir + '/manifest.json'

  // まず manifest.json を試す → あればマルチバッチ、なければ通常の単一tileset
  fetch(manifestUrl)
    .then(res => {
      if (!res.ok) throw new Error('no manifest')
      return res.json()
    })
    .then((manifest: { batches: string[] }) => {

      // バッチを段階的に追加（ブラウザの同時接続数制限を考慮）
      const CHUNK_SIZE = 4
      let i = 0
      function addNextChunk() {
        const end = Math.min(i + CHUNK_SIZE, manifest.batches.length)
        for (; i < end; i++) {
          const batch = manifest.batches[i]
          const batchUrl = `${baseDir}/${batch}/tileset.json`
          const batchId = `${id}/${batch}`
          const layer = createTile3DLayer(
            `pointcloud-${batchId}`, batchUrl, pointSize, opacity, geoidHeight, callbacks
          )
          layers.set(batchId, layer)
        }
        syncLayers(map)
        if (i < manifest.batches.length) {
          setTimeout(addNextChunk, 1500)
        } else {
        }
      }
      addNextChunk()
    })
    .catch(() => {
      // manifest.json がない → 通常の単一tileset
      const layer = createTile3DLayer(
        `pointcloud-${id}`, tilesetUrl, pointSize, opacity, geoidHeight, callbacks
      )
      layers.set(id, layer)
      syncLayers(map)
    })
}

/**
 * レイヤーのプロパティを更新（pointSize, opacity）
 * deck.glのレイヤーはimmutableなので、新しいインスタンスで置き換える
 * マルチバッチの場合、id/ プレフィックスで全バッチを一括更新
 */
export function updatePointCloudLayer(
  map: MaplibreMap,
  id: string,
  props: { pointSize?: number; opacity?: number }
): void {
  const layers = activeLayers.get(map)
  if (!layers) return

  let updated = false
  for (const [key, layer] of layers.entries()) {
    if (key === id || key.startsWith(id + '/')) {
      const updatedLayer = layer.clone(props)
      layers.set(key, updatedLayer)
      updated = true
    }
  }
  if (updated) syncLayers(map)
}

/**
 * 特定のレイヤーを削除（マルチバッチの場合は全バッチ削除）
 */
export function removePointCloudLayer(map: MaplibreMap, id: string): void {
  const layers = activeLayers.get(map)
  if (!layers) return
  let count = 0
  for (const key of Array.from(layers.keys())) {
    if (key === id || key.startsWith(id + '/')) {
      layers.delete(key)
      count++
    }
  }
  if (count > 0) {
    syncLayers(map)
  }
}

/**
 * 全レイヤーを削除（パネル閉じ時等）
 */
export function removeAllPointCloudLayers(map: MaplibreMap): void {
  const layers = activeLayers.get(map)
  if (layers) layers.clear()

  const overlay = overlayMap.get(map)
  if (overlay) {
    overlay.setProps({ layers: [] })
  }
}

/**
 * overlayを完全に破棄（コンポーネント破棄時）
 */
export function disposeOverlay(map: MaplibreMap): void {
  const overlay = overlayMap.get(map)
  if (overlay) {
    try {
      map.removeControl(overlay as unknown as maplibregl.IControl)
    } catch (e) {
      // マップが既に破棄されている場合はエラーを無視
    }
    overlayMap.delete(map)
    activeLayers.delete(map)
    layerBeforeIds.delete(map)
  }
}

/**
 * 点群レイヤーのz-order位置を設定
 * beforeId: このMapLibreレイヤーの「下」に点群を配置する
 * deck.glの_resolveLayers()がbeforeIdを使ってmoveLayer()を呼び、正しい位置に配置する
 * → reorderLayers()から直接moveLayer()を呼んでも_resolveLayers()に上書きされるため、
 *   この関数経由でbeforeIdを設定→syncLayers()で反映する
 */
export function setPointCloudBeforeId(
  map: MaplibreMap,
  id: string,
  beforeId: string | null
): void {
  let beforeIds = layerBeforeIds.get(map)
  if (!beforeIds) {
    beforeIds = new Map()
    layerBeforeIds.set(map, beforeIds)
  }

  const layers = activeLayers.get(map)
  if (!layers) return

  // 該当idの全レイヤー（マルチバッチ含む）にbeforeIdを設定
  for (const key of layers.keys()) {
    if (key === id || key.startsWith(id + '/')) {
      if (beforeId) {
        beforeIds.set(key, beforeId)
      } else {
        beforeIds.delete(key)
      }
    }
  }

  syncLayers(map)
}
